import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { addBusinessDays, isWeekend, format, parse } from 'date-fns';
import i18n from '../../../i18n';
import {
  DeliveryInfoContainer,
  DeliveryText,
  DeliveryScheduleWrapper,
  DeliveryScheduleHeader,
  DeliveryScheduleTitle,
  DeliveryScheduleList,
  DeliveryScheduleItem,
  RefreshButton,
  ErrorMessage,
  CalendarIconButton,
  DatePickerWrapper,
  DatePickerPortal,
} from './DeliveryInfo.style';

const DeliveryInfo = ({
  tyre,
  onDeliveryDateChange,
  onRefreshDelivery,
}) => {
  const { t } = useTranslation();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    if (tyre.selectedDeliveryDate) {
      return parse(tyre.selectedDeliveryDate, 'dd.MM.yyyy', new Date());
    }
    return null;
  });
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const calendarButtonRef = useRef(null);

  useEffect(() => {
    if (tyre.selectedDeliveryDate) {
      setSelectedDate(parse(tyre.selectedDeliveryDate, 'dd.MM.yyyy', new Date()));
    }
  }, [tyre.selectedDeliveryDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showDatePicker &&
        calendarButtonRef.current &&
        !calendarButtonRef.current.contains(event.target) &&
        !event.target.closest('.react-datepicker')
      ) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDatePicker]);

  const isWorkingDay = (date) => !isWeekend(date);

  const handleDateChange = (date) => {
    const formattedDate = format(date, 'dd.MM.yyyy');
    setSelectedDate(date);
    setShowDatePicker(false);
    if (onDeliveryDateChange) {
      onDeliveryDateChange(tyre._id, formattedDate);
    }
  };

  const handleCalendarIconClick = () => {
    if (!showDatePicker && calendarButtonRef.current) {
      const rect = calendarButtonRef.current.getBoundingClientRect();
      setPickerPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX,
      });
    }
    setShowDatePicker(!showDatePicker);
  };

  if (tyre.infoLoading) {
    return (
      <DeliveryInfoContainer>
        <i className="mdi mdi-loading mdi-spin" />
        <DeliveryText>{t('loadingDeliveryInfo')}</DeliveryText>
      </DeliveryInfoContainer>
    );
  }

  if (tyre.errorMessage) {
    return (
      <ErrorMessage>
        <i className="mdi mdi-alert" />
        {tyre.errorMessage}
      </ErrorMessage>
    );
  }

  if (tyre.isNotInStock) {
    return (
      <ErrorMessage>
        <i className="mdi mdi-package-variant-remove" />
        {t('notInStock')}
      </ErrorMessage>
    );
  }

  const renderDatePicker = () => {
    if (!showDatePicker) return null;

    return ReactDOM.createPortal(
      <DatePickerPortal
        style={{
          position: 'absolute',
          top: `${pickerPosition.top}px`,
          left: `${pickerPosition.left}px`,
          zIndex: 99999,
        }}
      >
        <DatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          minDate={
            tyre.deliveryDate
              ? parse(tyre.deliveryDate, 'dd.MM.yyyy', new Date())
              : addBusinessDays(new Date(), 1)
          }
          filterDate={isWorkingDay}
          inline
          dateFormat="dd.MM.yyyy"
          calendarStartDay={1}
          locale={i18n.language}
        />
      </DatePickerPortal>,
      document.body
    );
  };

  if (tyre.scheduleDetails && tyre.scheduleDetails.length > 0) {
    return (
      <>
        <DeliveryInfoContainer>
          <i className="mdi mdi-truck-delivery" />
          <DeliveryScheduleWrapper>
            <DeliveryScheduleHeader>
              <DeliveryScheduleTitle>{t('expectedDeliveryDate')}</DeliveryScheduleTitle>
              <RefreshButton
                onClick={() => onRefreshDelivery?.(tyre._id)}
                disabled={tyre.infoLoading}
                className={tyre.infoLoading ? 'spinning' : ''}
                title={t('inquiry')}
              >
                <i className="mdi mdi-refresh" />
              </RefreshButton>
              <DatePickerWrapper>
                <CalendarIconButton
                  ref={calendarButtonRef}
                  onClick={handleCalendarIconClick}
                  title={t('selectDeliveryDate')}
                >
                  <i className="mdi mdi-calendar" />
                </CalendarIconButton>
              </DatePickerWrapper>
            </DeliveryScheduleHeader>
            {tyre.selectedDeliveryDate ? (
              <DeliveryScheduleList>
                <DeliveryScheduleItem>
                  <span className="quantity">
                    {tyre.scheduleDetails.reduce((sum, s) => sum + s.quantity, 0)} {t('pcs')}
                  </span>
                  <span className="separator">→</span>
                  <span className="date">{tyre.selectedDeliveryDate}</span>
                </DeliveryScheduleItem>
              </DeliveryScheduleList>
            ) : (
              <DeliveryScheduleList>
                {tyre.scheduleDetails.map((schedule, index) => (
                  <DeliveryScheduleItem key={index}>
                    <span className="quantity">{schedule.quantity} {t('pcs')}</span>
                    <span className="separator">→</span>
                    <span className="date">{schedule.date}</span>
                  </DeliveryScheduleItem>
                ))}
                {tyre.notInStockQuantity > 0 && (
                  <DeliveryScheduleItem style={{ color: '#dc3545' }}>
                    <span className="quantity" style={{ color: '#dc3545' }}>{tyre.notInStockQuantity} {t('pcs')}</span>
                    <span className="separator" style={{ color: '#dc3545' }}>→</span>
                    <span className="date" style={{ color: '#dc3545' }}>{t('notInStock')}</span>
                  </DeliveryScheduleItem>
                )}
              </DeliveryScheduleList>
            )}
          </DeliveryScheduleWrapper>
        </DeliveryInfoContainer>
        {renderDatePicker()}
      </>
    );
  }

  if (tyre.deliveryDate) {
    return (
      <>
        <DeliveryInfoContainer>
          <i className="mdi mdi-truck-delivery" />
          <DeliveryScheduleWrapper>
            <DeliveryScheduleHeader>
              <DeliveryScheduleTitle>{t('expectedDeliveryDate')}</DeliveryScheduleTitle>
              <RefreshButton
                onClick={() => onRefreshDelivery?.(tyre._id)}
                disabled={tyre.infoLoading}
                className={tyre.infoLoading ? 'spinning' : ''}
                title={t('inquiry')}
              >
                <i className="mdi mdi-refresh" />
              </RefreshButton>
              <DatePickerWrapper>
                <CalendarIconButton
                  ref={calendarButtonRef}
                  onClick={handleCalendarIconClick}
                  title={t('selectDeliveryDate')}
                >
                  <i className="mdi mdi-calendar" />
                </CalendarIconButton>
              </DatePickerWrapper>
            </DeliveryScheduleHeader>
            <DeliveryText>
              <strong>{tyre.selectedDeliveryDate || tyre.deliveryDate}</strong>
            </DeliveryText>
          </DeliveryScheduleWrapper>
        </DeliveryInfoContainer>
        {renderDatePicker()}
      </>
    );
  }

  return null;
};

DeliveryInfo.propTypes = {
  tyre: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    deliveryDate: PropTypes.string,
    selectedDeliveryDate: PropTypes.string,
    scheduleDetails: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        quantity: PropTypes.number,
      })
    ),
    errorMessage: PropTypes.string,
    infoLoading: PropTypes.bool,
    isNotInStock: PropTypes.bool,
    notInStockQuantity: PropTypes.number,
  }).isRequired,
  onDeliveryDateChange: PropTypes.func,
  onRefreshDelivery: PropTypes.func,
};

export default DeliveryInfo;
